import React from 'react';
import { GenericModal } from 'src/components/Modals/GenericModal';
import { GenericModalStore } from './types';
import { create } from 'zustand';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { IScheduledActionsGroup } from 'src/store/modules/adManager/adScheduledActionGroups/types';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useStacklineTheme } from '@stackline/ui';
import colors from 'src/utils/colors';
import { AdCampaignManagerServiceClient } from 'src/utils/adCampaignManagerServiceClient';
import { AD_PLATFORM_TYPE, SCHEDULED_ACTION_STATUS } from 'sl-ad-campaign-manager-data-model';

export const useRuleData = create<GenericModalStore>((set) => ({
  isOpen: false,
  openModal: () => set({ isOpen: true }),
  closeModal: () => set({ isOpen: false }),
  data: null,
  setData: (data: IScheduledActionsGroup) => set({ data }),
  rootData: null,
  setRootData: (data: IScheduledActionsGroup[]) => set({ rootData: data }),
  updateDataState: null,
  setUpdateDataState: (data: (x: any) => void) => set({ updateDataState: data }),
  changeEvent: null,
  setChangeEvent: (data: React.ChangeEvent<HTMLInputElement>) => set({ changeEvent: data })
}));

const RulesUpdateModal = () => {
  const { isOpen, closeModal, data, rootData, updateDataState: setData, changeEvent: e } = useRuleData();

  const rulesImpacted = data?.scheduleDetail.appliesTo;

  const theme = useStacklineTheme();
  const { showSnackbar } = useSnackbar();
  const mainEntity = useAppSelector((state) => state?.entityService?.mainEntity) ?? undefined;
  const updateStatus = async () => {
    try {
      const res = await AdCampaignManagerServiceClient.updateScheduledActionStatus({
        platformType: mainEntity?.platformType as AD_PLATFORM_TYPE,
        scheduledActionGroupId: data?.scheduledActions[0]?.scheduledAction.scheduledActionGroupId,
        status: !e?.target.checked ? SCHEDULED_ACTION_STATUS.ENABLED : SCHEDULED_ACTION_STATUS.PAUSED
      });
      const updatedData = rootData.map((x) => {
        if (x?.id === data?.id) {
          x.scheduledActions[0].scheduledAction.status = res.data.status;
        }
        return x;
      });
      setData(updatedData);
      showSnackbar({
        message: `Your rule was successfully updated.`,
        type: 'success'
      });
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'An error occurred. Please reach out to support@stackline.com for further guidance.',
        type: 'error'
      });
    }
  };

  return (
    <GenericModal
      open={isOpen}
      onClose={closeModal}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: theme.spacing.sm,
          width: '500px',
          padding: '24px'
        },
        '.genericModal__header': {
          paddingBottom: '12px'
        }
      }}
    >
      <GenericModal.Header title="Confirm rule status update" onClose={closeModal} />
      <GenericModal.Body>
        <Text
          variant="body2"
          sx={{
            marginBottom: '20px'
          }}
        >
          Are you sure you want to update this rule? This change will impact {rulesImpacted}.
        </Text>
        <Flex justifyContent="flex-end">
          <SlButton
            onClick={() => {
              closeModal();
            }}
            buttonSx={{
              border: `1px solid ${colors.graysSecondary}`,
              backgroundColor: 'white',
              padding: '6px 20px',
              '&:hover': {
                backgroundColor: 'white'
              }
            }}
            textSx={{
              color: colors.darkBlue
            }}
          >
            Cancel
          </SlButton>
          <SlButton
            onClick={async () => {
              await updateStatus();
              closeModal();
            }}
            buttonSx={{
              backgroundColor: colors.darkBlue,
              marginLeft: '16px',
              border: `1px solid ${colors.darkBlue}`,
              padding: '6px 20px',
              '&:hover': {
                backgroundColor: colors.darkBlue
              }
            }}
            textSx={{
              color: colors.white
            }}
          >
            Update
          </SlButton>
        </Flex>
      </GenericModal.Body>
    </GenericModal>
  );
};

RulesUpdateModal.show = () => {
  useRuleData.getState().openModal();
};

RulesUpdateModal.setData = (data: IScheduledActionsGroup) => {
  useRuleData.getState().setData(data);
};

RulesUpdateModal.setRootData = (data: IScheduledActionsGroup[]) => {
  useRuleData.getState().setRootData(data);
};

RulesUpdateModal.setUpdateDataState = (data: (x: any) => void) => {
  useRuleData.getState().setUpdateDataState(data);
};

RulesUpdateModal.setChangeEvent = (data: React.ChangeEvent<HTMLInputElement>) => {
  useRuleData.getState().setChangeEvent(data);
};

export default RulesUpdateModal;
